import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useToastStore } from "store/toastStore";
import { useStore } from "store/zustand";
import { useNavigate } from 'react-router-dom';
import { checkUserAirlines } from "apis/myRequestsService";
import RequestReplyTab from "../../pages/Request/RequestReplyTab";

// Define an interface for the submission data
interface SubmissionData {
  // Add properties based on what data the RequestReplyTab submits
  // For example:
  message?: string;
  loads?: string;
  // Add other fields as needed
}

// Define an interface for the flight detail
interface FlightDetail {
  airlineId:number;  
  logoImg: string;
  airlineName: string;
  flightNumber: string;
  flightDate: string;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
  flightDuration: string;
  requestDetailId: number;
}

const SharePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { isAuthenticated } = useStore();
    const navigate = useNavigate();
    
    // Use the FlightDetail interface for the state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFlightDetail, setSelectedFlightDetail] = useState<FlightDetail | null>(null);

    // Extract flight details from URL
    const requestDetailId = searchParams.get("requestDetailId");
    const airlineName = searchParams.get("airlineName") || "Unknown Airline";
    const arrival = searchParams.get("arrival") || "";
    const departure = searchParams.get("departure") || "";
    const sourceCode = searchParams.get("sourceCode") || "Unknown";
    const destinationCode = searchParams.get("destinationCode") || "Unknown";
    const flightNo = searchParams.get("flightNo") || "N/A";
    const airlineId = searchParams.get("airlineId") || "0";
    
    // Format date as "Month Day"
    const formatDate = (dateString: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    };

    // Format time in 24-hour format "HH:mm"
    const formatTime = (dateString: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    };

    const calculateFlightDuration = (departure:string, arrival:string) => {
        const departureTime = new Date(departure).getTime();
        const arrivalTime = new Date(arrival).getTime();
        const durationInHours = ((arrivalTime - departureTime) / 3600000).toFixed(1);
        return `${durationInHours}h`;
      };

    // Add functions to handle modal closure and form submission
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedFlightDetail(null);
    };

 // Change the handleSubmit function to match the expected signature
const handleSubmit = () => {
    console.log('Form submitted');
    
    // Handle the form submission logic here
    handleCloseModal();
    navigate("/search");
};

    const handleAnswerClick = async () => {
        if (!isAuthenticated) {
          useToastStore.getState().showToast(
            "You must have to login first",
            "error",
            3000
          );
          navigate("/login"); // Redirect to login page
          return;
        }
    
        try {
          const response = await checkUserAirlines(Number(airlineId));
          console.log('Check User Airlines response:', response);
          if (response) {
            // Create a FlightDetail object
            const flightDetail: FlightDetail = {
                logoImg: "", // You might need a default or fetch this from somewhere
                airlineId: Number(airlineId),
                airlineName: airlineName,
                flightNumber: flightNo,
                flightDate: formatDate(departure),
                departureAirport: sourceCode,
                arrivalAirport: destinationCode,
                departureTime: formatTime(departure),
                arrivalTime: formatTime(arrival),
                flightDuration: calculateFlightDuration(departure, arrival), // Calculate this or fetch from somewhere
                requestDetailId: Number(requestDetailId) // Using airlineId as requestDetailId, adjust if needed
            };
            
            setSelectedFlightDetail(flightDetail);
            setIsModalOpen(true);
          } else {
            useToastStore.getState().showToast(
              "You can't answer to this airline",
              "error",
              3000
            );
          }
        } catch (error) {
          console.error('Error calling checkUserAirlines:', error);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
            <div className="bg-blue-100 p-8 rounded-lg w-full max-w-xl mx-auto border border-blue-300 shadow-lg">
                <div className="text-center mb-8">
                    <h2 className="text-xl font-semibold text-gray-700">
                        I requested the loads for Flight {flightNo} on Standby Travel
                    </h2>
                    <p className="text-gray-600 italic mt-2">
                        Answer my request in Standby Travel App to get free credits
                    </p>
                </div>

                <div className="bg-white p-6 rounded-md border border-gray-300 mb-10">
                    <div className="flex items-center justify-center space-x-6">
                        <div className="flex flex-col items-center mr-2">
                            <div className="h-16 w-16 rounded-full flex items-center justify-center mb-3">
                               
                                <img src={`/assets/logos/${airlineId}.jpg`}/>
                            </div>
                            <span className="text-sm text-gray-600">{airlineName}</span>
                        </div>

                        <div className="flex-1">
                            <div className="flex justify-between mb-4">
                                <span className="font-bold text-lg">{flightNo}</span>
                                <span className="font-bold text-lg">
                                    {sourceCode} <span className="mx-1">→</span> {destinationCode}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-700 text-base">{formatDate(departure)}</span>
                                <div className="flex space-x-8">
                                    <span className="text-gray-700 text-base">{formatTime(departure)}</span>
                                    <span className="text-gray-700 text-base">{formatTime(arrival)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <button 
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-4 px-6 rounded-md transition-colors text-lg"
                onClick={handleAnswerClick}
                >
                    Answer in Standby Travel
                </button>
            </div>
            
            {/* Add the RequestReplyTab component to render when selectedFlightDetail is set */}
            {selectedFlightDetail && (
                <RequestReplyTab
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSubmit={handleSubmit}
                    airlineId={selectedFlightDetail.airlineId}
                    logoImg={selectedFlightDetail.logoImg}
                    airlineName={selectedFlightDetail.airlineName}
                    flightNumber={selectedFlightDetail.flightNumber}
                    flightDate={selectedFlightDetail.flightDate}
                    departureAirport={selectedFlightDetail.departureAirport}
                    arrivalAirport={selectedFlightDetail.arrivalAirport}
                    departureTime={selectedFlightDetail.departureTime}
                    arrivalTime={selectedFlightDetail.arrivalTime}
                    flightDuration={selectedFlightDetail.flightDuration}
                    requestDetailId={selectedFlightDetail.requestDetailId}
                />
            )}
        </div>
    );
};

export default SharePage;